import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import MainSidebar from 'components/ui/MainSidebar';
import ApplicationsTable from './components/ApplicationsTable';
import ApplicationDetailPanel from './components/ApplicationDetailPanel';
import StatsRow from './components/StatsRow';
import FilterBar from './components/FilterBar';
import ToastStack from './components/ToastStack';

export default function AdminApplicationReviewDashboard() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', sector: 'all', fundingStage: 'all' });
  const [toasts, setToasts] = useState([]);
  const [flashRows, setFlashRows] = useState({}); // { [id]: 'green' | 'red' }

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, ...toast }]);
    if (toast?.autoDismiss !== false) {
      setTimeout(() => removeToast(id), toast?.duration || 3500);
    }
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  const flashRow = (appId, color) => {
    setFlashRows(prev => ({ ...prev, [appId]: color }));
    setTimeout(() => setFlashRows(prev => { const n = { ...prev }; delete n?.[appId]; return n; }), 500);
  };

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase?.from('showcase_applications')?.select('*')?.order('submitted_at', { ascending: false });
      if (filters?.status !== 'all') query = query?.eq('status', filters?.status);
      if (filters?.sector !== 'all') query = query?.eq('sector', filters?.sector);
      if (filters?.fundingStage !== 'all') query = query?.eq('funding_stage', filters?.fundingStage);
      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      setApplications(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApplications(); }, [filters]);

  // Optimistic update helper
  const optimisticUpdate = (appId, newStatus) => {
    setApplications(prev => prev?.map(a => a?.id === appId ? { ...a, status: newStatus } : a));
    setSelectedApp(prev => prev?.id === appId ? { ...prev, status: newStatus } : prev);
  };

  const handleApprove = async (app, setActionLoading) => {
    setActionLoading('approve');
    // Optimistic update
    optimisticUpdate(app?.id, 'approved');
    flashRow(app?.id, 'green');
    await new Promise(r => setTimeout(r, 600));
    try {
      const { error: updateErr } = await supabase?.from('showcase_applications')?.update({
        status: 'approved',
        reviewed_at: new Date()?.toISOString()
      })?.eq('id', app?.id);
      if (updateErr) throw updateErr;
      addToast({
        type: 'approve',
        title: 'Business Approved!',
        message: `${app?.business_name} is now live on Investment Opportunities.`,
        duration: 4000,
      });
    } catch (err) {
      // Revert optimistic update
      optimisticUpdate(app?.id, app?.status);
      flashRow(app?.id, 'red');
      const toastId = addToast({
        type: 'error',
        title: 'Could not publish to Investment Opportunities.',
        message: 'Application status updated but listing failed.',
        autoDismiss: false,
        retryFn: () => { removeToast(toastId); handleApprove(app, setActionLoading); },
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (app, notes, setActionLoading) => {
    setActionLoading('reject');
    optimisticUpdate(app?.id, 'rejected');
    flashRow(app?.id, 'red');
    await new Promise(r => setTimeout(r, 600));
    try {
      const { error: updateErr } = await supabase?.from('showcase_applications')?.update({
        status: 'rejected',
        admin_notes: notes,
        reviewed_at: new Date()?.toISOString()
      })?.eq('id', app?.id);
      if (updateErr) throw updateErr;
      addToast({
        type: 'navy',
        message: `Application rejected. ${app?.business_name} has been notified.`,
        duration: 3000,
      });
    } catch (err) {
      optimisticUpdate(app?.id, app?.status);
      flashRow(app?.id, 'red');
      const isNetwork = !navigator.onLine;
      addToast({
        type: 'error',
        message: isNetwork
          ? 'Connection lost. Check your internet and retry.' :'Action failed. Please try again.',
        autoDismiss: false,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnderReview = async (app, setActionLoading) => {
    setActionLoading('underReview');
    optimisticUpdate(app?.id, 'under_review');
    flashRow(app?.id, 'navy');
    await new Promise(r => setTimeout(r, 600));
    try {
      const { error: updateErr } = await supabase?.from('showcase_applications')?.update({
        status: 'under_review',
        reviewed_at: new Date()?.toISOString()
      })?.eq('id', app?.id);
      if (updateErr) throw updateErr;
      addToast({
        type: 'navy',
        message: `Review requested. ${app?.founder_name} will be notified.`,
        duration: 3000,
      });
    } catch (err) {
      optimisticUpdate(app?.id, app?.status);
      addToast({
        type: 'error',
        message: !navigator.onLine
          ? 'Connection lost. Check your internet and retry.' :'Action failed. Please try again.',
        autoDismiss: false,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  const stats = {
    total: applications?.length,
    pending: applications?.filter(a => a?.status === 'pending')?.length,
    approved: applications?.filter(a => a?.status === 'approved')?.length,
    thisWeek: applications?.filter(a => {
      const d = new Date(a.submitted_at);
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return d >= weekAgo;
    })?.length,
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-surface)' }}>
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className={`transition-all duration-250 min-h-screen flex flex-col pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:ml-[64px]' : 'lg:ml-[240px]'}`}>
        {/* Header */}
        <div className="px-4 md:px-6 lg:px-8 pt-6 pb-4" style={{ background: 'var(--color-primary)' }}>
          <div className="flex items-start justify-between gap-4 pl-10 lg:pl-0">
            <div>
              <h1 className="font-heading font-bold text-white text-2xl md:text-3xl">Application Review</h1>
              <p className="font-caption text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Review and process business showcase applications
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(133,113,77,0.15)', border: '1px solid rgba(133,113,77,0.3)' }}>
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="font-caption text-xs text-white">Admin Panel</span>
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 md:px-6 lg:px-8 py-6 pb-24 lg:pb-6">
          <StatsRow stats={stats} />
          <FilterBar filters={filters} onFilterChange={setFilters} />

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#D4A520" strokeWidth="3" strokeDasharray="30 70"/>
                </svg>
                <p className="text-sm" style={{ color: '#6B7280' }}>Loading applications...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={fetchApplications} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: '#D4A520', color: '#0B1F3B' }}>Retry</button>
            </div>
          ) : (
            <ApplicationsTable
              applications={applications}
              onSelect={setSelectedApp}
              selectedId={selectedApp?.id}
              flashRows={flashRows}
              onApprove={handleApprove}
              onReject={handleReject}
              onUnderReview={handleUnderReview}
            />
          )}
        </div>
      </div>

      {/* Detail Panel */}
      {selectedApp && (
        <ApplicationDetailPanel
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onUnderReview={handleUnderReview}
        />
      )}

      {/* Toast Stack */}
      <ToastStack toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
